import { useEffect } from "react"
import * as PropTypes from "prop-types"
import { I18n } from "react-polyglot-hooks"
import minBy from "lodash/minBy"
import ModelScene from "scenes/ModelScene"
import ModelContextProvider from "scenes/ModelScene/ModelContext"
import LoadingScene from "scenes/LoadingScene"
import { useRouter } from "next/router"
import { sendCarView } from "scripts/FacebookPixel"
import { getModelData, getModelsList } from "services/webService"
import { parseMDX } from "lib/mdx"
import car from "../../../../../i18n/es/car"
import tModel from "../../../../../i18n/es/model"
import car_data from "../../../../../i18n/es/car_data"
import common from "../../../../../i18n/es/common"
import brand_i18n from "../../../../../i18n/es/brand"
import { getFooterLinks, getSeoModelData } from "../../../../../src/services/seoService"

export default function Model({ brand, model, seoData, cars, offers, footerLinks }) {
  const { isFallback } = useRouter()

  useEffect(() => {
    if (cars.length) {
      sendCarView(
        cars.map(c => c.id),
        minBy(cars, "minimumPrice")
      )
    }
  }, [cars])

  if (isFallback)
    return (
      <I18n locale="es" phrases={{ ...common, ...brand_i18n }}>
        <LoadingScene />
      </I18n>
    )

  // if (notFound) return <NotFound noIndex={false} />

  return (
    <I18n locale="es" phrases={{ ...common, ...car, ...car_data, ...tModel, ...brand_i18n }}>
      <ModelContextProvider brand={brand} model={model} cars={cars} offers={offers} seoData={seoData} footerLinks={footerLinks} sellingStatus="new">
        <ModelScene />
      </ModelContextProvider>
    </I18n>
  )
}

Model.defaultProps = {
  footerLinks: [],
  brand: {},
  model: {},
  seoData: {},
  cars: [],
  offers: []
}

Model.propTypes = {
  footerLinks: PropTypes.array,
  brand: PropTypes.object,
  model: PropTypes.object,
  seoData: PropTypes.object,
  cars: PropTypes.array,
  offers: PropTypes.array
}

export async function getStaticPaths() {
  const res = await getModelsList("es")
  const data = await res.json()
  const paths = process.env.SKIP_PRELOAD || !Array.isArray(data) ? [] : data.map(item => ({ params: item }));
  return { paths, fallback: "blocking" }
}

export async function getStaticProps({ params }) {
  try {
    const res = await getModelData(params.brand, params.model, "es")
    const resSeo = await getSeoModelData(params.brand, params.model)

    if (res.status === 404) {
      return {
        redirect: {
          destination: `/es/renting-${params.brand}`,
          permanent: true
        },
        revalidate: 60
      }
    }

    // Footer Links
    const footerLinksRes = await getFooterLinks()
    const footerLinks = await footerLinksRes.json()

    const data = await res.json()
    const seoData = await resSeo.json()

    const { brand = {}, model = {}, cars = [], offers = [] } = data
    const parsedMdx = await parseMDX(model.description)

    const youtubeId = model?.youtubeId ?? model?.youtube_id ?? null

    return {
      props: {
        brand,
        model: {
          ...model,
          youtubeId,
          description: parsedMdx
        },
        seoData,
        cars,
        offers,
        footerLinks
      },
      revalidate: 30
    }
  } catch (error) {
    console.error('Error en getStaticProps de model:', error);
    return {
      props: { notFound: true },
      revalidate: 60
    };
  }
}
